<template>
  <div class="test-heading">
    <div class="case-creation__card">
      <div class="text-information-card">
        <div class="case-creation__heading border-bottom-none">
          <div class="title-with-icon">
            <span class="icon">
              <img
                src="@/assets/images/icons/icon-test-info.svg"
                alt="icon"
                class="img-fluid"
              />
            </span>
            <h3 class="title">Order Codes</h3>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="case-creation__body pb-0">
    <el-row :gutter="15">
      <el-col :span="24">
        <div
          class="input-value view-cases order-codes"
          v-if="hasOrderCodes"
        >
          <ul>
            <li
              v-for="(code, index) in orderCodes"
              :key="`order-code-${code}-${index}`"
            >
              <el-tooltip
                v-if="isRestrictedPending(code)"
                content="Approval Pending"
                placement="top"
                effect="dark"
              >
                <span class="restricted-code">{{ code }}</span>
              </el-tooltip>
              <span v-else>{{ code }}</span>
            </li>
          </ul>
        </div>
        <div
          class="input-value view-cases order-codes collected-tests"
          v-if="hasCollectedTests"
        >
          <p class="collected-tests-label">MLX Collected Tests</p>
          <ul>
            <li
              v-for="(test, index) in collectedTests"
              :key="`collected-test-${test}-${index}`"
            >
              <span>{{ test }}</span>
            </li>
          </ul>
        </div>
      </el-col>
    </el-row>

    <!-- REJECTED ORDER CODES -->
    <el-row v-if="hasRejectedOrderGroups" :gutter="15" class="rejected-orders-section">
      <el-col :span="24">

        <div class="rejected-orders-card">

          <!-- HEADER -->
          <div class="rejected-orders-header">

            <div class="rejected-header-content">

              <div class="rejected-icon">
                <i class="el-icon-warning-outline"></i>
              </div>

              <div>
                <h4>
                  Rejected Order Codes
                </h4>

                <span class="rejected-subtitle">
                  {{ rejectedOrderCodesCount }}
                  rejected test{{
                    rejectedOrderCodesCount !== 1
                      ? "s"
                      : ""
                  }}
                </span>
              </div>

            </div>

          </div>

          <!-- COLUMN HEADER -->
          <div class="rejection-column-header">

            <div>
              Rejected Order Code
            </div>

            <div>
              Rejection Reason
            </div>

          </div>

          <!-- REJECTION RECORDS -->
          <div class="rejected-orders-list">

            <div v-for="(rejection, index) in rejectedOrderGroups" :key="`rejection-group-${index}`"
              class="rejection-row">

              <!-- LEFT SIDE -->
              <div class="rejection-codes">

                <div class="code-list">

                  <span v-for="(code, codeIndex) in rejection.order_codes" :key="`rejected-code-${index}-${codeIndex}`"
                    class="rejected-code">
                    {{ code }}
                  </span>

                </div>

              </div>

              <!-- RIGHT SIDE -->
              <div class="rejection-reason">

                <div class="reason-text">
                  {{ rejection.reason || "-" }}
                </div>

                <div v-if="rejection.tube_names.length" class="tube-info">
                  <span class="tube-label">
                    Tube{{ rejection.tube_names.length > 1 ? "s" : "" }}:
                  </span>

                  <span class="tube-value">
                    {{ rejection.tube_names.join(", ") }}
                  </span>
                </div>

              </div>

            </div>

          </div>

        </div>

      </el-col>
    </el-row>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import UserPermissionsHelper from "@/mixins/UserPermissionsHelper";

export default {
  name: "ViewTests",
  mixins: [UserPermissionsHelper],
  computed: {
    ...mapGetters("orders", ["getCaseInformation"]),
    // Computed property for order codes to simplify template
    orderCodes() {
      return this.getCaseInformation?.order_codes || [];
    },
    rejectedOrderGroups() {
      const rejectionData =
        this.getCaseInformation
          ?.order_rejection_data || [];

      const caseTubeIds = new Set(
        rejectionData
          .filter(
            (rejection) =>
              String(rejection.rejection_level || "").toUpperCase() ===
              "CASE"
          )
          .flatMap((rejection) =>
            Array.isArray(rejection.tube_ids) ? rejection.tube_ids : []
          )
          .map(String)
      );
      const groupedRejections = new Map();

      rejectionData
        .filter((rejection) => {
          const level = String(
            rejection.rejection_level || ""
          ).toUpperCase();
          const tubeIds = Array.isArray(rejection.tube_ids)
            ? rejection.tube_ids.map(String)
            : [];

          return (
            (level === "CASE" || level === "TUBE") &&
            !(level === "TUBE" &&
              tubeIds.some((tubeId) => caseTubeIds.has(tubeId)))
          );
        })
        .forEach((rejection) => {
          const orderCodes = Array.isArray(rejection.order_codes)
            ? rejection.order_codes.filter(Boolean).map(String)
            : [];
          const reason =
            rejection.reason ||
            rejection.rejection_reason ||
            "-";

          if (!orderCodes.length) return;

          const group = groupedRejections.get(reason) || {
            order_codes: new Set(),
            reason,
            tube_names: [],
          };

          orderCodes.forEach((code) => group.order_codes.add(code));
          groupedRejections.set(reason, group);
        });

      return Array.from(groupedRejections.values()).map((rejection) => ({
        ...rejection,
        order_codes: Array.from(rejection.order_codes),
      }));
    },

    hasRejectedOrderGroups() {
      return this.rejectedOrderGroups.length > 0;
    },

    rejectedOrderCodesCount() {
      const uniqueCodes = new Set();

      this.rejectedOrderGroups.forEach(
        (rejection) => {
          rejection.order_codes.forEach(
            (code) => uniqueCodes.add(code)
          );
        }
      );

      return uniqueCodes.size;
    },
    hasOrderCodes() {
      return this.orderCodes.length > 0;
    },
    collectedTests() {
      return this.getCaseInformation?.collected_tests || [];
    },
    hasCollectedTests() {
      return this.collectedTests.length > 0;
    },
    restrictedPendingSet() {
      const restricted = this.getCaseInformation?.restricted_order_codes;
      if (
        restricted &&
        Array.isArray(restricted.order_codes) &&
        restricted.order_codes.length > 0 &&
        String(restricted.status || "").toUpperCase() === "PENDING"
      ) {
        return new Set(restricted.order_codes);
      }
      return new Set();
    },
  },
  methods: {
    isRestrictedPending(code) {
      return this.restrictedPendingSet.has(code);
    },
  },
};
</script>

<style lang="scss">
.test-heading {
  .case-creation__card {
    .text-information-card {
      display: flex;
      justify-content: space-between;
    }
  }
}
.case-creation__body {
  background: #ffffff !important;
}
.collected-tests {
  margin-top: 12px;

  .collected-tests-label {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 6px 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

}
.order-codes.collected-tests li {
  font-size: 11px;
  padding: 3px 9px;
}
.order-codes {
  ul {
    display: flex;
    flex-wrap: wrap; // wraps to next line if needed
    gap: 10px;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  li {
    background: #eaf3ff;
    color: #1f3a8a;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;

    .restricted-code {
      color: #d97706;
      cursor: default;
    }

    &:has(.restricted-code) {
      background: #fff7ed;
      border: 1px solid #fbbf24;
      color: #d97706;
    }
  }
}


/* =========================
   REJECTED ORDER SECTION
========================= */

.rejected-orders-section {
  margin-top: 24px;
}

.rejected-orders-card {
  overflow: hidden;

  border: 1px solid #fecaca;
  border-radius: 10px;

  background: #ffffff;
}


/* =========================
   HEADER
========================= */

.rejected-orders-header {
  padding: 16px 18px;

  background: #fff7f7;

  border-bottom: 1px solid #fecaca;
}

.rejected-header-content {
  display: flex;
  align-items: center;

  gap: 12px;
}

.rejected-icon {
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  border-radius: 50%;

  background: #fee2e2;

  color: #dc2626;

  font-size: 18px;
}

.rejected-orders-header {
  h4 {
    margin: 0;

    font-size: 15px;
    font-weight: 600;

    color: #991b1b;
  }
}

.rejected-subtitle {
  display: block;

  margin-top: 3px;

  font-size: 12px;

  color: #9f1239;
}


/* =========================
   COLUMN HEADERS
========================= */

.rejection-column-header {
  display: grid;

  grid-template-columns:
    minmax(0, 1.5fr) minmax(240px, 1fr);

  gap: 20px;

  padding: 10px 16px;

  background: #f9fafb;

  border-bottom: 1px solid #e5e7eb;

  font-size: 11px;
  font-weight: 600;

  text-transform: uppercase;
  letter-spacing: 0.4px;

  color: #6b7280;
}


/* =========================
   REJECTION ROW
========================= */

.rejection-row {
  display: grid;

  grid-template-columns:
    minmax(0, 1.5fr) minmax(240px, 1fr);

  gap: 20px;

  padding: 16px;

  border-bottom: 1px solid #e5e7eb;

  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fffafa;
  }
}

.rejected-orders-list {
  max-height: 280px;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: #fca5a5 #fff7f7;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #fff7f7;
  }

  &::-webkit-scrollbar-thumb {
    border: 2px solid #fff7f7;
    border-radius: 8px;
    background: #fca5a5;
  }
}


/* =========================
   REJECTED CODES
========================= */

.code-list {
  display: flex;
  flex-wrap: wrap;

  gap: 7px;
}

.rejected-code {
  display: inline-flex;
  align-items: center;

  padding: 5px 10px;

  border-radius: 14px;

  background: #fee2e2;

  border: 1px solid #fecaca;

  color: #b91c1c;

  font-size: 12px;
  font-weight: 500;

  line-height: 1.3;
}


/* =========================
   REASON
========================= */

.rejection-reason {
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  gap: 8px;
}

.reason-text {
  display: inline-flex;
  align-items: center;

  padding: 6px 10px;

  border-radius: 6px;

  background: #fff7ed;

  border: 1px solid #fed7aa;

  color: #9a3412;

  font-size: 13px;
  font-weight: 500;
}


/* =========================
   TUBE INFORMATION
========================= */

.tube-info {
  display: flex;
  align-items: center;

  flex-wrap: wrap;

  gap: 5px;

  font-size: 12px;
}

.tube-label {
  font-weight: 600;

  color: #6b7280;
}

.tube-value {
  color: #374151;
}


/* =========================
   MOBILE
========================= */

@media (max-width: 768px) {

  .rejection-column-header {
    display: none;
  }

  .rejection-row {
    grid-template-columns: 1fr;

    gap: 14px;
  }

  .rejection-codes::before {
    content: "Rejected Order Code";

    display: block;

    margin-bottom: 8px;

    font-size: 11px;
    font-weight: 600;

    text-transform: uppercase;

    color: #6b7280;
  }

  .rejection-reason::before {
    content: "Rejection Reason";

    font-size: 11px;
    font-weight: 600;

    text-transform: uppercase;

    color: #6b7280;
  }
}
</style>
